<!-- 
*****************************************************************************
**                                                                         **
** Copyright (c) 2012-2024, Tyker Technology BV                            **
** All rights reserved.                                                    **
**                                                                         **
** This software is the confidential and proprietary information of        **
** Tyker Technology BV. Unauthorized copying or disclosure of this file,   **
** via any medium is strictly prohibited. This file is only available to   **
** authorized individuals with the permission of the copyright holders.    **
** If you encounter this file and do not have permission, please contact   ** 
** the copyright holders and delete this file.                             **
**                                                                         **
***************************************************************************** 
-->

<template>
  <v-app>
    <MenuList ref="menu" :toolbar="false" v-on:menuSelected="menuSelected" :customerName="customerName"
      :userName="userName" />
    <LoginPopup />
    <AppLoadingPopup ref="loading" />
    <CustomerSelect />

    <component v-bind:is="currentViewComponent" v-dynamic-events="knownEvents" :tasks="tasks" :map="mapRef"
      v-on:refreshProjections="refreshProjections" />

    <div id="main" :style="{ background: $vuetify.theme.background }">

      <!-- MAP BASED ON DIFFERENT SUBSCRIPTION -->
      <Map v-if="!isRBPSubscription" ref="map" :rprProductionTable="$refs.rprProductionTable"
        :rbpProductionTable="$refs.rbpProductionTable" :loading="$refs.loading" v-on:updateLayerlist="onUpdateLayerlist"
        v-on:onAddAlert="onAddAlert" v-on:onRemoveAlert="onRemoveAlert" v-on:heightmap="onHeightmap"
        v-on:mapMotionplanEditor="onMapMotionplanEditor" v-on:setLineData="setLineData"
        v-on:setActiveLayer="setActiveLayer" v-on:hideTaskInfoAndLayerlist="hideTaskInfoAndLayerlist"
        v-on:showTaskInfoAndLayerlist="showTaskInfoAndLayerlist" />

      <RBPMap v-else ref="rbpmap" :rbpProductionTable="$refs.rbpProductionTable" :loading="$refs.loading"
        v-on:updateLayerlist="onUpdateLayerlist" v-on:onAddAlert="onAddAlert" v-on:onRemoveAlert="onRemoveAlert"
        v-on:mapMotionplanEditor="onMapMotionplanEditor" v-on:closeMotionplanEditor="onCloseMotionplanEditor"
        v-on:setLineData="setLineData" v-on:setActiveLayer="setActiveLayer"
        v-on:hideTaskInfoAndLayerlist="hideTaskInfoAndLayerlist"
        v-on:showTaskInfoAndLayerlist="showTaskInfoAndLayerlist" v-on:createMotionplan="onCreateMotionplan"
        v-on:combineMotionplans="onCombineMotionplans"
        v-on:currentUnit="onCurrentUnit" />

      <!-- TASKLIST BASED ON DIFFERENT SUBSCRIPTION -->
      <TaskListProfiler v-if="isRPRSubscription" ref="tasklist" v-on:tasksFromTasklist="tasksFromTasklist"
        v-on:openTask="openTask" v-on:menuListClick="onMenuListClick" v-on:menuSelected="menuSelected" />

      <TaskList v-if="isRBPSubscription" ref="tasklist" v-on:tasksFromTasklist="tasksFromTasklist"
        v-on:openTask="openTask" v-on:menuListClick="onMenuListClick" v-on:menuSelected="menuSelected" />

      <TaskListVector v-if="isVCTSubscription" ref="tasklistvector" v-on:menuListClick="onMenuListClick"
        v-on:menuSelected="menuSelected" v-on:openTask="openTask" v-on:openCalendar="openCalendar" :map="$refs.map" />


      <ControlPanel ref="controlPanel" v-on:onAddAlert="onAddAlert" v-on:onRemoveAlert="onRemoveAlert"
        v-on:menuSelected="menuSelected" v-on:openTask="openTask" v-on:closeTask="closeTask" :map="mapRef" />
      <!-- <Calendar
        ref="calendar"
        v-on:openTask="openTask"
        v-on:closeTask="closeTask"
      /> -->
      <MapHeightmap ref="heightmap" v-if="isRPRSubscription" v-on:changed="onHeightmapChanged"
        v-on:changePopup="onChangePopup" />

      <MapMotionplanEditor ref="mapMotionplanEditor" :map="mapRef" v-on:openTask="openTask" v-on:closeTask="closeTask"
        v-on:openUploadDXF="openUploadDXF" />

      <MapMotionplanCreator ref="mapMotionplanCreator" :map="mapRef" v-on:openTask="openTask" v-on:closeTask="closeTask"
        v-on:toggleMotionplanCreator="onToggleMotionplanCreator" />

      <TaskInfo ref="taskinfo" v-on:closeTask="closeTask" v-on:onAddAlert="onAddAlert" v-on:openTask="openTask"
        v-on:menuListClick="onMenuListClick" v-on:updateTasks="updateTasks"
        v-on:toggleMotionplanCreator="onToggleMotionplanCreator"
        v-on:toggleMotionplanEditor="onToggleMotionplanEditor" />

      <MapLayerSwitcherFloating ref="layerswitchter" v-if="mounted" :map="mapRef" :selection="true"
        v-bind:isDisabled="false" :subscriptionGroup="subscriptionGroup" v-on:changePopup="onChangePopup"
        v-on:openTask="openTask" v-on:closeTask="closeTask" v-on:menuSelected="menuSelected"
        v-on:openUploadDXF="openUploadDXF" v-on:dxfToLinelog="onDxfToLinelog" />

      <UploadDXF ref="uploadDXF" :map="mapRef" v-on:openTask="openTask" v-on:closeTask="closeTask" />

      <AdminDiagnostics ref="diagnostics" v-on:menuListClick="onMenuListClick" :map="mapRef" />

      <component id="rprProductionTable" ref="rprProductionTable" v-bind:is="rprProductionTable"
        v-on:changePopup="onChangePopup" />

      <component id="rbpProductionTable" ref="rbpProductionTable" v-bind:is="rbpProductionTable"
        v-on:changePopup="onChangePopup" v-on:selectProduction="onSelectRBPProductionFromList" />

      <AppAlerts id="alerts" :alerts="alerts" />
    </div>
  </v-app>
</template>

<script>
import MenuList from "./components/MenuList";
import Map from "./components/Map";
import TaskList from "./components/TaskListCopy";
import TaskListProfiler from "./components/TaskList";
import TaskListVector from "./components/vector/Tasklist"
import ControlPanel from "./components/vector/ControlPanel"
import NotificationCenter from "./components/vector/NotificationCenter"
import Calendar from "./components/vector/Calendar"
import TaskInfo from "./components/TaskInfo";
import MapLayerSwitcherFloating from "./components/MapLayerSwitcherFloating";
import LoginPopup from "./components/LoginPopup";
import CustomerUserDetails from "./components/CustomerUserDetails";
import CustomerUserList from "./components/CustomerUserList";
import ProjectList from "./components/ProjectList";
import CustomerSelect from "./components/CustomerSelect";
import TaskCreateRBP from "./components/robotplotter/TaskCreate";
import TaskCreateRPR from "./components/roadprofiler/TaskCreate";
import TaskCreateVCT from "./components/vector/TaskCreate";
import RPRProductionViewer from "./components/roadprofiler/ProductionViewer";
import RBPProductionViewer from "./components/robotplotter/ProductionViewer";
import AppAlerts from "./components/AppAlerts";
import RPRProductionFloating from "./components/roadprofiler/ProductionListFloating";
import RPRSupport from "./components/roadprofiler/SupportPage";
import RBPProductionFloating from "./components/robotplotter/ProductionListFloating";
import RBPSupport from "./components/robotplotter/SupportPage";
import AppLoadingPopup from "./components/AppLoadingPopup";
import MapHeightmap from "./components/roadprofiler/MapHeightmap";

import SettingsMenu from "./components/SettingsMenu";
import AdminDiagnostics from "./components/AdminDiagnostics.vue";
import UploadDXF from "./components/UploadDXF.vue";

import MapMotionplanCreator from "./components/robotplotter/MapMotionplanCreator.vue";
import MapMotionplanEditor from "./components/robotplotter/MapMotionplanEditor";
import RBPMap from "./components/robotplotter/RBPMap.vue";

import * as CONST from "./Constants.js";

import { getCookie } from './models/Cookies.js';

export default {
  name: "Civil",

  components: {
    MenuList,
    Map,
    TaskList,
    TaskListProfiler,
    TaskListVector,
    ControlPanel,
    NotificationCenter,
    Calendar,
    TaskInfo,
    MapLayerSwitcherFloating,
    LoginPopup,
    CustomerUserDetails,
    CustomerUserList,
    ProjectList,
    CustomerSelect,
    TaskCreateRBP,
    TaskCreateRPR,
    TaskCreateVCT,
    RPRProductionViewer,
    RBPProductionViewer,
    AppAlerts,
    RPRProductionFloating,
    RPRSupport,
    RBPProductionFloating,
    RBPSupport,
    AppLoadingPopup,
    MapHeightmap,
    MapMotionplanEditor,
    SettingsMenu,
    AdminDiagnostics,
    UploadDXF,
    RBPMap,
    MapMotionplanCreator
  },
  props: {},
  data: () => ({
    customerName: null,
    userName: null,

    tasks: [],
    mounted: false,
    currentViewComponent: null,
    rprProductionTable: RPRProductionFloating,
    rbpProductionTable: RBPProductionFloating,
    knownEvents: {
      openTask: "openTask",
      close: "closeComponent",
      taskCreated: "onTaskCreated",
      updateProjects: "updateTasklistProjects",
      getTasks: "getTasksFromTasklist",
    },
    alerts: [],
    mapRef: null
  }),
  computed: {
    //TODO all unneeded now fix later
    isRPRSubscription() {
      return this.$auth.subscription == "__SUB_CIVIL_ROADPROFILER";
    },
    isRBPSubscription() {
      return this.$auth.subscription == "__SUB_CIVIL_ROADPLOTTER" 
    },
    isVCTSubscription() {
      return this.$auth.subscription == "__SUB_CIVIL_VECTOR";
    },
    subscriptionGroup() {
      if (this.isRBPSubscription) return "__SUB_CIVIL_ROADPLOTTER"
      if (this.isRPRSubscription) return "__SUB_CIVIL_ROADPROFILER"
      if (this.isVCTSubscription) return "__SUB_CIVIL_VECTOR"
      return null;
    },
    isLoggedIn() {
      return this.$auth.isAuthenticated && this.$auth.customerId !== null;
    },
  },
  mounted: function () {
    //this.onAddAlert("opentask", "Example warning 2", "warning", true, "tooltip");
    this.mounted = true;
    this.getMapRef()
  },
  methods: {
    onCurrentUnit(unit){
      this.$refs.taskinfo.setUnit(unit);
    },
    onDxfToLinelog() {
      this.mapRef.getLinelogState = !this.mapRef.getLinelogState
    },
    onCombineMotionplans(mp1, mp2, reverseMp1, reverseMp2) {
      this.$refs.mapMotionplanEditor.connect(mp1, mp2, reverseMp1, reverseMp2)
    },
    onCreateMotionplan(motionplanData, handle) {
      this.$refs.mapMotionplanCreator.createMotionplan(motionplanData, handle)
    },
    onToggleMotionplanEditor() {
      // zoom to motionplan?
      this.mapRef.toggleMotionplanEditor(true)
    },
    onToggleMotionplanCreator() {
      this.mapRef.createMotionPlanState = !this.mapRef.createMotionPlanState

      if (this.mapRef.createMotionPlanState) {
        this.$refs.taskinfo.hide();
        this.$refs.mapMotionplanCreator.show()
      } else {
        this.$refs.taskinfo.show();
      }
    },
    getMapRef() {
      if (this.isRBPSubscription) {
        this.mapRef = this.$refs.rbpmap;
      } else {
        this.mapRef = this.$refs.map;
      }
    },
    refreshProjections() {
      this.mapRef.refreshProjections();
    },
    setActiveLayer(id) {
      this.$refs.layerswitchter.setActiveLayer(id);
    },
    onChangePopup(popup) {
      switch (popup) {
        case "layerswitcher":
          if (this.$refs.heightmap) this.$refs.heightmap.hideHeightmap();
          this.$refs.rprProductionTable.hideProductionlist();
          this.$refs.rbpProductionTable.hideProductionlist();
          break;
        case "heightmap":
          this.$refs.layerswitchter.hideLayers();
          this.$refs.rprProductionTable.hideProductionlist();

          break;
        case "productionlist":
          this.$refs.layerswitchter.hideLayers();
          if (this.$refs.heightmap) this.$refs.heightmap.hideHeightmap();
          break;
        default:
          this.$refs.layerswitchter.hideLayers();
          if (this.$refs.heightmap) this.$refs.heightmap.hideHeightmap();
          this.$refs.rprProductionTable.hideProductionlist();
          break;
      }
    },
    setLineData(lineData, taskId) {
      this.$refs.controlPanel.setLineData(lineData, taskId);
      if (this.$refs.calendar.selectingLine) {
        this.$refs.calendar.onLineSelected(lineData);
        this.$refs.taskinfo.closeTask();
        this.$refs.rprProductionTable.hideProductionlist();
        this.$refs.tasklistvector.hideTaskList();
      }
    },
    hideTaskInfoAndLayerlist() {
      this.$refs.taskinfo.hide();
      this.$refs.layerswitchter.hideLayers();
    },
    showTaskInfoAndLayerlist() {
      this.$refs.taskinfo.taskinfoVisible = true;
      this.$refs.layerswitchter.showLayers();
    },
    async openTask(taskId) {
      // console.log(typeof task);
      // if (typeof task != "task") {
      // }
      this.$refs.loading.show()
      this.$refs.loading.setText(this.language.t("Map.loadingTaskInfo"))
      if (this.$auth.subscription != "__SUB_CIVIL_VECTOR") {
        this.$refs.tasklist.hideTaskList();
      } else {
        this.$refs.tasklistvector.hideTaskList();
      }

      let res = await this.$axios.get(route("api.task", taskId))

      let task = res.data

      try {
        this.$refs.taskinfo.openTask(task);
        if (this.$auth.subscription == "__SUB_CIVIL_VECTOR") { this.$refs.controlPanel.setSelectedTask(true, task.taskId); }
        this.$selectedTask.taskId = task.taskId;
      } catch (error) {
        console.error(error);
      }

      try {
        this.mapRef.openTask(task);
      } catch (error) {
        console.error(error);
      }
    },
    updateTasks() {
      if (this.$auth.subscription != "__SUB_CIVIL_VECTOR") {
        //this.$refs.tasklist.getTasks();
      } else {
        this.$refs.tasklistvector.getTasks();
      }
    },
    closeTask() {
      var taskSelected = !!this.mapRef.task;

      this.mapRef.closeTask();
      if (this.$auth.subscription != "__SUB_CIVIL_VECTOR") {
        this.$refs.tasklist.closeTask();
      } else {
        if (taskSelected) this.$refs.tasklistvector.hideTaskList();
        this.$refs.controlPanel.setSelectedTask(false, null);
      }
      this.alerts = this.alerts.filter((alert) => alert.id !== "opentask");
    },
    onUpdateLayerlist() {
      this.$refs.layerswitchter.update();
    },
    onMenuListClick() {
      this.$refs.menu.open();
    },
    updateTasklistProjects() {
      console.log("updateTasklistProjects");
      this.$refs.tasklist.getProjects();
    },
    menuSelected(menu) {
      switch (menu) {
        case "taskCreate":
          if (this.$auth.subscription == "__SUB_CIVIL_ROADPROFILER") {
            this.currentViewComponent = "TaskCreateRPR";
          } else if (this.$auth.subscription == "__SUB_CIVIL_VECTOR") {
            this.currentViewComponent = "TaskCreateVCT";
          } else {
            this.currentViewComponent = "TaskCreateRBP";
          }
          break;
        case "adminDiagnostics":
          this.$refs.diagnostics.toggleDiagnostics();
          break;
        case "ProductionViewer":
          if (this.$auth.subscription == "__SUB_CIVIL_ROADPROFILER") {
            this.currentViewComponent = "RPRProductionViewer";
          } else {
            this.currentViewComponent = "RBPProductionViewer";
          }
          break;
        case "support":
          if (this.$auth.subscription == "__SUB_CIVIL_ROADPROFILER") {
            this.currentViewComponent = "RPRSupport";
          } else {
            this.currentViewComponent = "RBPSupport";
          }
          break;
        case "customerUserList":
          this.currentViewComponent = "CustomerUserList";
          break;
        case "accountDetails":
          this.currentViewComponent = "CustomerUserDetails";
          break;
        case "projectList":
          this.currentViewComponent = "ProjectList";
          break;
        case "settingsMenu":
          this.currentViewComponent = "SettingsMenu";
          break;
        case "notificationCenter":
          this.currentViewComponent = "NotificationCenter";
          break;
        default:
          console.error(`Undefined menu item ${menu}`);
          break;
      }
    },
    onAddAlert(id, text, type, dismissible = false, tooltip = "") {
      console.log(`Add alert ${id}: ${text}`);
      this.alerts.push({
        id: id,
        text: text,
        type: type,
        dismissible: dismissible,
        tooltip: tooltip,
      });
    },
    tasksFromTasklist(tasks) {
      this.tasks = tasks;
    },
    onRemoveAlert(id) {
      this.alerts = this.alerts.filter((alert) => alert.id !== id);
    },
    closeComponent() {
      this.currentViewComponent = null;
    },
    onTaskCreated() {
      if (this.$auth.subscription != "__SUB_CIVIL_VECTOR") {
        //this.$refs.tasklist.getTasks();
      } else {
        this.$refs.tasklistvector.tasks = null
        this.$refs.tasklistvector.allTasks = null;
        this.$refs.tasklistvector.getTasks();
      }
    },
    onHeightmap(data) {
      if (this.$refs.heightmap) this.$refs.heightmap.setData(data);
    },
    onHeightmapChanged(settings) {
      this.mapRef.setHeightmap(settings);
    },
    onMapMotionplanEditor(motionplan, segment) {
      if (this.$refs.mapMotionplanEditor) this.$refs.mapMotionplanEditor.setSegment(motionplan, segment);
    },
    onCloseMotionplanEditor() {
      if (this.$refs.mapMotionplanEditor) this.$refs.mapMotionplanEditor.close();
    },
    onSelectRBPProductionFromList(id) {
      this.mapRef.selectRBPProductionFromList(id);
    },
    openCalendar() {
      this.$refs.calendar.dialog = true;
    },
    openUploadDXF() {
      this.$refs.uploadDXF.dialog = true;
    }
  },
  watch: {
    isLoggedIn(val) {
      if (val) {
        this.$axios.get(route('api.customer.accountdetails'))
          .then((res) => {
            this.customerName = res.data.dataCustomer.name;
            this.userName = res.data.dataAccount.user.firstName + " " +
              (res.data.dataAccount.user.middlename ?
                res.data.dataAccount.user.middlename + " " :
                "") +
              res.data.dataAccount.user.lastName
            this.getMapRef() // move to better location

            let x = getCookie('selectedUnitSystem')
            if (!x) {
              if (res.data.dataCustomer.metric == 0) {
                this.$selectedUnitSystem = 'im'
              } 
            } 

          }).catch(() => {
            this.loginError = 'NoCustomers';
          });

      }
    },
    '$auth.isAuthenticated'(val) {
      this.$axios.get(route('api.user.language')).then(res => {
        //change language if language isnt saved in cookies
        let x = getCookie('language');
        if (!x) this.language.locale = res.data.code.toLowerCase()
      });
    }
  },
  filters: {},
  /* bind events from the dynamic component to methods, use knownEvents to set this up */
  directives: {
    DynamicEvents: {
      bind: (el, binding, vnode) => {
        const allEvents = binding.value;
        Object.keys(allEvents).forEach((event) => {
          // register handler in the dynamic component
          vnode.componentInstance.$on(event, (eventData) => {
            const targetEvent = allEvents[event];
            vnode.context[targetEvent](eventData);
          });
        });
      },
      unbind: function (el, binding, vnode) {
        vnode.componentInstance.$off();
      },
    },
  },
};
</script>

<style>
#main {
  height: 100%;
}

#alerts {
  left: 0;
  right: 0;
  margin: auto;
  width: 80%;
}

.v-application--wrap {
  /* fix proper height for android */
  min-height: 0vh !important;
}
</style>